import JSZip from 'jszip';

export interface ExtractedExcelImages {
  byRow: Record<number, string>;
  byCell: Record<string, string>;
  mediaList: string[];
}

/**
 * Extracts embedded images (Excel 365 "Place in Cell" & Classic Drawings) from an Excel .xlsx ArrayBuffer.
 */
export async function extractExcelImages(buffer: ArrayBuffer): Promise<ExtractedExcelImages> {
  const byRow: Record<number, string> = {};
  const byCell: Record<string, string> = {};
  const mediaList: string[] = [];

  try {
    const zip = await JSZip.loadAsync(buffer);

    // 1. Build media cache: mediaPath -> Data URL
    const mediaCache: Record<string, string> = {};
    const mediaFiles = Object.keys(zip.files).filter(f => f.startsWith('xl/media/') && !zip.files[f].dir);

    // Sort media files naturally (image1, image2, ..., image10)
    mediaFiles.sort((a, b) => {
      const numA = parseInt(a.match(/\d+/)?.[0] || '0', 10);
      const numB = parseInt(b.match(/\d+/)?.[0] || '0', 10);
      return numA - numB;
    });

    for (const path of mediaFiles) {
      const file = zip.files[path];
      if (!file) continue;
      const base64 = await file.async('base64');
      const ext = path.split('.').pop()?.toLowerCase() || 'png';
      const mime = ext === 'jpg' || ext === 'jpeg' ? 'image/jpeg' : ext === 'svg' ? 'image/svg+xml' : `image/${ext}`;
      const dataUrl = `data:${mime};base64,${base64}`;
      const cleanPath = path.replace(/^xl\//, ''); // e.g. media/image1.png
      mediaCache[cleanPath] = dataUrl;
      mediaCache[path] = dataUrl;
      mediaList.push(dataUrl);
    }

    // 2. Excel 365 "Place in Cell" (RichData)
    try {
      const metadataXml = await zip.file('xl/metadata.xml')?.async('text');
      const rdrichvalueXml = await zip.file('xl/richData/rdrichvalue.xml')?.async('text');
      const richValueRelXml = await zip.file('xl/richData/richValueRel.xml')?.async('text');
      const relsXml = await zip.file('xl/richData/_rels/richValueRel.xml.rels')?.async('text');

      const rels: Record<string, string> = {};
      if (relsXml) {
        let m: RegExpExecArray | null;
        const relRx = /<Relationship\s+[^>]*Id="([^"]+)"[^>]*Target="([^"]+)"/g;
        while ((m = relRx.exec(relsXml)) !== null) {
          rels[m[1]] = m[2].replace(/^\.\.\//, '');
        }
      }

      const richValueRels: string[] = [];
      if (richValueRelXml) {
        let m: RegExpExecArray | null;
        const rvrRx = /<rel\s+[^>]*r:id="([^"]+)"/g;
        while ((m = rvrRx.exec(richValueRelXml)) !== null) richValueRels.push(m[1]);
      }

      const rdrichvalues: (number | null)[] = [];
      if (rdrichvalueXml) {
        let m: RegExpExecArray | null;
        const rvRx = /<rv[^>]*>(.*?)<\/rv>/gs;
        while ((m = rvRx.exec(rdrichvalueXml)) !== null) {
          const vm = m[1].match(/<v>(\d+)<\/v>/);
          rdrichvalues.push(vm ? parseInt(vm[1], 10) : null);
        }
      }

      const valueMetadata: number[] = [];
      if (metadataXml) {
        let m: RegExpExecArray | null;
        const rcRx = /<rc\s+[^>]*t="1"\s+v="(\d+)"/g;
        while ((m = rcRx.exec(metadataXml)) !== null) valueMetadata.push(parseInt(m[1], 10));
      }

      const sheetFiles = Object.keys(zip.files).filter(f => f.startsWith('xl/worksheets/sheet'));
      for (const sheetPath of sheetFiles) {
        const sheetXml = await zip.file(sheetPath)?.async('text');
        if (!sheetXml) continue;
        let m: RegExpExecArray | null;
        const rowRx = /<row\s+r="(\d+)"[^>]*>(.*?)<\/row>/gs;
        while ((m = rowRx.exec(sheetXml)) !== null) {
          const rowNum = parseInt(m[1], 10);
          const rowContent = m[2];
          let cm: RegExpExecArray | null;
          const cellVmRx = /<c\s+r="([A-Z]+)\d+"[^>]*vm="(\d+)"/g;
          while ((cm = cellVmRx.exec(rowContent)) !== null) {
            const colLetter = cm[1];
            const vmIndex = parseInt(cm[2], 10) - 1;
            if (vmIndex >= 0 && vmIndex < valueMetadata.length) {
              const vIndex = valueMetadata[vmIndex];
              if (vIndex !== undefined && vIndex < rdrichvalues.length) {
                const relIndex = rdrichvalues[vIndex];
                if (relIndex !== null && relIndex < richValueRels.length) {
                  const rId = richValueRels[relIndex];
                  const mediaPath = rels[rId];
                  if (mediaPath && mediaCache[mediaPath]) {
                    const dataUrl = mediaCache[mediaPath];
                    byCell[`${colLetter}${rowNum}`] = dataUrl;
                    if (!byRow[rowNum]) byRow[rowNum] = dataUrl;
                  }
                }
              }
            }
          }
        }
      }
    } catch (e) {
      console.warn('RichData extraction warning:', e);
    }

    // 3. Classic Excel Drawings (Anchored Images)
    try {
      const drawingFiles = Object.keys(zip.files).filter(f => f.startsWith('xl/drawings/drawing'));
      for (const drawingPath of drawingFiles) {
        if (drawingPath.includes('_rels')) continue;
        const drawingXml = await zip.file(drawingPath)?.async('text');
        const drawingNum = drawingPath.match(/drawing(\d+)\.xml/)?.[1];
        const relsPath = `xl/drawings/_rels/drawing${drawingNum}.xml.rels`;
        const relsXml = await zip.file(relsPath)?.async('text');
        if (!drawingXml || !relsXml) continue;

        const rels: Record<string, string> = {};
        let rm: RegExpExecArray | null;
        const relRx = /<Relationship\s+[^>]*Id="([^"]+)"[^>]*Target="([^"]+)"/g;
        while ((rm = relRx.exec(relsXml)) !== null) {
          rels[rm[1]] = rm[2].replace(/^\.\.\//, '');
        }

        let am: RegExpExecArray | null;
        const anchorRx = /<xdr:(?:twoCellAnchor|oneCellAnchor)[^>]*>(.*?)<\/xdr:(?:twoCellAnchor|oneCellAnchor)>/gs;
        while ((am = anchorRx.exec(drawingXml)) !== null) {
          const anchorContent = am[1];
          const rowMatch = anchorContent.match(/<xdr:from>.*?<xdr:row>(\d+)<\/xdr:row>.*?<\/xdr:from>/s);
          const colMatch = anchorContent.match(/<xdr:from>.*?<xdr:col>(\d+)<\/xdr:col>.*?<\/xdr:from>/s);
          const blipMatch = anchorContent.match(/<a:blip[^>]*r:embed="([^"]+)"/);

          if (rowMatch && blipMatch) {
            const rowNum = parseInt(rowMatch[1], 10) + 1; // 0-indexed to 1-indexed
            const colIdx = colMatch ? parseInt(colMatch[1], 10) : 0;
            const colLetter = String.fromCharCode(65 + colIdx);
            const rId = blipMatch[1];
            const mediaPath = rels[rId];
            if (mediaPath && mediaCache[mediaPath]) {
              const dataUrl = mediaCache[mediaPath];
              byCell[`${colLetter}${rowNum}`] = dataUrl;
              if (!byRow[rowNum]) byRow[rowNum] = dataUrl;
            }
          }
        }
      }
    } catch (e) {
      console.warn('Drawing extraction warning:', e);
    }
  } catch (err) {
    console.error('Error loading Excel zip file for image extraction:', err);
  }

  return { byRow, byCell, mediaList };
}
